import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    Typography,
    Paper,
    Fade,
} from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import RatingChart from './RatingChart';
import Donut from './Donut';
import { Stars, formatWhen } from './utils';

interface RatingsTabProps {
    items: any[];
    totals: { total: number; rated: number; feedback: number };
    chartPoints: { label: string; value: number }[];
    daysRange: number;
    setDaysRange: (n: number) => void;
}

export default function RatingsTab({
    items,
    totals,
    chartPoints,
    daysRange,
    setDaysRange,
}: RatingsTabProps) {
    const displayItems = items;

    return (
        <Fade in timeout={400}>
            <Box>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Chart Card */}
                    <Grid item xs={12} lg={8}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <RatingChart
                                    points={chartPoints}
                                    daysRange={daysRange}
                                    setDaysRange={setDaysRange}
                                    usingDummy={items.length === 0}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Donut Chart Card */}
                    <Grid item xs={12} lg={4}>
                        <Card sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 200,
                        }}>
                            <CardContent>
                                {(() => {
                                    const total = (totals && totals.total) ? totals.total : items.length;
                                    const rated = (totals && typeof totals.rated === 'number' && totals.total)
                                        ? totals.rated
                                        : items.filter((s: any) => typeof s.rating === 'number' && s.rating > 0).length;
                                    return <Donut value={rated} total={total} label={'Rated'} />;
                                })()}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Feedback List */}
                <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            <StarIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#f59e0b' }} />
                            Recent Feedback
                        </Typography>

                        <Stack spacing={2}>
                            {displayItems.map((it: any, i: number) => (
                                <Paper
                                    key={i}
                                    elevation={0}
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        borderRadius: 3,
                                        bgcolor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: 'grey.100',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                                        spacing={2}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {it.candidateName || it.lastRatingBy || 'Unknown candidate'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 1,
                                                    whiteSpace: 'pre-wrap',
                                                    fontStyle: it.lastFeedback ? 'normal' : 'italic',
                                                }}
                                            >
                                                {it.lastFeedback || 'No feedback provided'}
                                            </Typography>
                                        </Box>

                                        <Box sx={{
                                            textAlign: { xs: 'left', sm: 'right' },
                                            minWidth: 120,
                                        }}>
                                            <Box sx={{ mb: 1 }}>
                                                <Stars value={it.rating ?? null} />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatWhen(it) || '—'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </Fade>
    );
}
